import React, { useState, useContext, useCallback, useEffect } from "react";
import {
  View,
  TextInput,
  Text,
  TouchableOpacity,
  StyleSheet,
  Alert,
  Linking,
  ScrollView,
  Image,
  Platform,
  useWindowDimensions,
  ActivityIndicator
} from "react-native";
import * as ImagePicker from "expo-image-picker";
import * as DocumentPicker from "expo-document-picker";
import { AuthContext } from "../../Context/AuthContext";
import { Ionicons } from "@expo/vector-icons";
import { getFirestore, doc, setDoc } from "firebase/firestore";
import { firebaseApp } from "../../firebase";
import GlobalStyles from "../../components/StylenColor";
import { useFocusEffect } from "@react-navigation/native";
import {
  widthPercentageToDP as wp,
  heightPercentageToDP as hp,
} from "react-native-responsive-screen";
import { db } from "../../firebase";
import { useDesktopDetect, DashboardLayout, DesktopTheme } from "../../components/web";

// Desktop Feedback View Component
const DesktopFeedbackView = ({
  employeeId,
  setEmployeeId,
  email,
  setEmail,
  feedback,
  setFeedback,
  images,
  pdf,
  handleImageUpload,
  handlePDFUpload,
  handleDeleteImage,
  handleDeletePdf,
  handleSubmit,
  handleCall,
  isSubmitting,
}) => {
  const [hoveredField, setHoveredField] = useState(null);
  const [hoveredBtn, setHoveredBtn] = useState(null);

  const wordCount = feedback.trim().split(/\s+/).filter(Boolean).length;

  return (
    <DashboardLayout currentRoute="Feedback">
      <ScrollView style={desktopStyles.container} showsVerticalScrollIndicator={false}>
        {/* Header */}
        <View style={desktopStyles.header}>
          <View style={desktopStyles.headerLeft}>
            <Text style={desktopStyles.pageTitle}>Feedback & Support</Text>
            <Text style={desktopStyles.pageSubtitle}>
              Share your feedback or get in touch with our support team
            </Text>
          </View>
        </View>

        {/* Main Content */}
        <View style={desktopStyles.contentWrapper}>
          {/* Feedback Form Card */}
          <View style={desktopStyles.formCard}>
            <View style={desktopStyles.formHeader}>
              <Ionicons name="chatbox-ellipses-outline" size={24} color={DesktopTheme.colors.primary} />
              <Text style={desktopStyles.formTitle}>Submit Feedback</Text>
            </View>

            <View style={desktopStyles.formRow}>
              {/* Left Column */}
              <View style={desktopStyles.formColumn}>
                <View style={desktopStyles.inputGroup}>
                  <Text style={desktopStyles.label}>Employee ID</Text>
                  <View style={[
                    desktopStyles.inputWrapper,
                    hoveredField === 'empId' && desktopStyles.inputWrapperFocused
                  ]}>
                    <Ionicons name="person-outline" size={18} color={DesktopTheme.colors.textMuted} style={desktopStyles.inputIcon} />
                    <TextInput
                      value={employeeId}
                      onChangeText={setEmployeeId}
                      style={desktopStyles.input}
                      placeholder="Enter your Employee ID"
                      placeholderTextColor={DesktopTheme.colors.textMuted}
                      onFocus={() => setHoveredField('empId')}
                      onBlur={() => setHoveredField(null)}
                    />
                  </View>
                </View>

                <View style={desktopStyles.inputGroup}>
                  <Text style={desktopStyles.label}>Email Address</Text>
                  <View style={[
                    desktopStyles.inputWrapper,
                    hoveredField === 'email' && desktopStyles.inputWrapperFocused
                  ]}>
                    <Ionicons name="mail-outline" size={18} color={DesktopTheme.colors.textMuted} style={desktopStyles.inputIcon} />
                    <TextInput
                      value={email}
                      onChangeText={setEmail}
                      style={desktopStyles.input}
                      placeholder="Enter your Email"
                      keyboardType="email-address"
                      placeholderTextColor={DesktopTheme.colors.textMuted}
                      onFocus={() => setHoveredField('email')}
                      onBlur={() => setHoveredField(null)}
                    />
                  </View>
                </View>

                {/* Attachments Section */}
                <View style={desktopStyles.attachmentsSection}>
                  <Text style={desktopStyles.label}>Attachments (Optional)</Text>
                  <View style={desktopStyles.uploadButtons}>
                    <TouchableOpacity
                      style={[
                        desktopStyles.uploadBtn,
                        pdf && desktopStyles.uploadBtnDisabled,
                        hoveredBtn === 'image' && !pdf && desktopStyles.uploadBtnHover,
                      ]}
                      onPress={handleImageUpload}
                      disabled={!!pdf}
                      onMouseEnter={() => setHoveredBtn('image')}
                      onMouseLeave={() => setHoveredBtn(null)}
                    >
                      <Ionicons name="image-outline" size={20} color={pdf ? DesktopTheme.colors.textMuted : DesktopTheme.colors.primary} />
                      <Text style={[desktopStyles.uploadBtnText, pdf && desktopStyles.uploadBtnTextDisabled]}>
                        Upload Image
                      </Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={[
                        desktopStyles.uploadBtn,
                        images.length > 0 && desktopStyles.uploadBtnDisabled,
                        hoveredBtn === 'pdf' && images.length === 0 && desktopStyles.uploadBtnHover,
                      ]}
                      onPress={handlePDFUpload}
                      disabled={images.length > 0}
                      onMouseEnter={() => setHoveredBtn('pdf')}
                      onMouseLeave={() => setHoveredBtn(null)}
                    >
                      <Ionicons name="document-outline" size={20} color={images.length > 0 ? DesktopTheme.colors.textMuted : DesktopTheme.colors.primary} />
                      <Text style={[desktopStyles.uploadBtnText, images.length > 0 && desktopStyles.uploadBtnTextDisabled]}>
                        Upload PDF
                      </Text>
                    </TouchableOpacity>
                  </View>

                  {/* Preview Area */}
                  {(images.length > 0 || pdf) && (
                    <View style={desktopStyles.previewArea}>
                      {images.map((img, index) => (
                        <View key={index} style={desktopStyles.previewItem}>
                          <Image
                            source={{ uri: `data:image/jpeg;base64,${img.base64}` }}
                            style={desktopStyles.previewImage}
                          />
                          <TouchableOpacity
                            style={desktopStyles.deleteBtn}
                            onPress={() => handleDeleteImage(index)}
                          >
                            <Ionicons name="trash-outline" size={16} color={DesktopTheme.colors.error} />
                          </TouchableOpacity>
                        </View>
                      ))}
                      {pdf && (
                        <View style={desktopStyles.pdfPreviewItem}>
                          <Ionicons name="document-text" size={32} color={DesktopTheme.colors.error} />
                          <Text style={desktopStyles.pdfName} numberOfLines={1}>{pdf.name || "PDF"}</Text>
                          <TouchableOpacity
                            style={desktopStyles.deleteBtnPdf}
                            onPress={handleDeletePdf}
                          >
                            <Ionicons name="close-circle" size={20} color={DesktopTheme.colors.error} />
                          </TouchableOpacity>
                        </View>
                      )}
                    </View>
                  )}
                </View>
              </View>

              {/* Right Column - Feedback Text */}
              <View style={desktopStyles.formColumn}>
                <View style={desktopStyles.inputGroup}>
                  <View style={desktopStyles.labelRow}>
                    <Text style={desktopStyles.label}>Your Feedback</Text>
                    <Text style={[
                      desktopStyles.wordCount,
                      wordCount > 900 && desktopStyles.wordCountWarning,
                    ]}>
                      {wordCount}/1000 words
                    </Text>
                  </View>
                  <View style={[
                    desktopStyles.textAreaWrapper,
                    hoveredField === 'feedback' && desktopStyles.inputWrapperFocused
                  ]}>
                    <TextInput
                      value={feedback}
                      onChangeText={(text) => {
                        const count = text.trim().split(/\s+/).filter(Boolean).length;
                        if (count <= 1000) setFeedback(text);
                      }}
                      style={desktopStyles.textArea}
                      multiline
                      placeholder="Share your thoughts, suggestions, or report any issues..."
                      placeholderTextColor={DesktopTheme.colors.textMuted}
                      onFocus={() => setHoveredField('feedback')}
                      onBlur={() => setHoveredField(null)}
                    />
                  </View>
                </View>
              </View>
            </View>

            {/* Submit Button */}
            <TouchableOpacity
              style={[
                desktopStyles.submitBtn,
                hoveredBtn === 'submit' && desktopStyles.submitBtnHover,
              ]}
              onPress={handleSubmit}
              onMouseEnter={() => setHoveredBtn('submit')}
              onMouseLeave={() => setHoveredBtn(null)}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <ActivityIndicator size="small" color="#fff" />
              ) : (
                <>
                  <Ionicons name="send-outline" size={20} color="#fff" />
                  <Text style={desktopStyles.submitBtnText}>Submit Feedback</Text>
                </>
              )}
            </TouchableOpacity>
          </View>

          {/* Support Card */}
          <View style={desktopStyles.supportCard}>
            <Ionicons name="headset-outline" size={40} color={DesktopTheme.colors.primary} />
            <Text style={desktopStyles.supportTitle}>Need Direct Support?</Text>
            <Text style={desktopStyles.supportText}>
              Our support team is available to help you with any questions or concerns.
            </Text>
            <TouchableOpacity
              style={[
                desktopStyles.callBtn,
                hoveredBtn === 'call' && desktopStyles.callBtnHover,
              ]}
              onPress={handleCall}
              onMouseEnter={() => setHoveredBtn('call')}
              onMouseLeave={() => setHoveredBtn(null)}
            >
              <Ionicons name="call-outline" size={20} color="#fff" />
              <Text style={desktopStyles.callBtnText}>Call Support</Text>
            </TouchableOpacity>
          </View>
        </View>
      </ScrollView>
    </DashboardLayout>
  );
};

// Desktop Styles
const desktopStyles = StyleSheet.create({
  container: {
    flex: 1,
    padding: DesktopTheme.spacing.lg,
    backgroundColor: DesktopTheme.colors.contentBg,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: DesktopTheme.spacing.lg,
  },
  headerLeft: {
    flex: 1,
  },
  pageTitle: {
    fontSize: DesktopTheme.typography.fontSize.h2,
    fontFamily: DesktopTheme.typography.fontFamily.heading,
    color: DesktopTheme.colors.textPrimary,
    marginBottom: 4,
  },
  pageSubtitle: {
    fontSize: DesktopTheme.typography.fontSize.body,
    fontFamily: DesktopTheme.typography.fontFamily.body,
    color: DesktopTheme.colors.textSecondary,
  },
  contentWrapper: {
    flexDirection: 'row',
    gap: DesktopTheme.spacing.lg,
    alignItems: 'flex-start',
  },
  formCard: {
    flex: 1,
    backgroundColor: DesktopTheme.colors.cardBg,
    borderRadius: DesktopTheme.borderRadius.xl,
    padding: DesktopTheme.spacing.xl,
    ...DesktopTheme.shadows.card,
  },
  formHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: DesktopTheme.spacing.sm,
    marginBottom: DesktopTheme.spacing.lg,
    paddingBottom: DesktopTheme.spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: DesktopTheme.colors.border,
  },
  formTitle: {
    fontSize: DesktopTheme.typography.fontSize.h3,
    fontFamily: DesktopTheme.typography.fontFamily.heading,
    color: DesktopTheme.colors.textPrimary,
  },
  formRow: {
    flexDirection: 'row',
    gap: DesktopTheme.spacing.xl,
  },
  formColumn: {
    flex: 1,
  },
  inputGroup: {
    marginBottom: DesktopTheme.spacing.md,
  },
  label: {
    fontSize: DesktopTheme.typography.fontSize.body,
    fontFamily: DesktopTheme.typography.fontFamily.body,
    color: DesktopTheme.colors.textPrimary,
    fontWeight: '600',
    marginBottom: DesktopTheme.spacing.sm,
  },
  labelRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: DesktopTheme.spacing.sm,
  },
  wordCount: {
    fontSize: DesktopTheme.typography.fontSize.small,
    fontFamily: DesktopTheme.typography.fontFamily.body,
    color: DesktopTheme.colors.textMuted,
  },
  wordCountWarning: {
    color: DesktopTheme.colors.warning,
  },
  inputWrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: DesktopTheme.colors.contentBg,
    borderRadius: DesktopTheme.borderRadius.md,
    borderWidth: 1,
    borderColor: DesktopTheme.colors.border,
    paddingHorizontal: DesktopTheme.spacing.md,
  },
  inputWrapperFocused: {
    borderColor: DesktopTheme.colors.primary,
    backgroundColor: '#ffffff',
  },
  inputIcon: {
    marginRight: DesktopTheme.spacing.sm,
  },
  input: {
    flex: 1,
    height: 44,
    fontSize: DesktopTheme.typography.fontSize.body,
    fontFamily: DesktopTheme.typography.fontFamily.body,
    color: DesktopTheme.colors.textPrimary,
    outlineStyle: 'none',
  },
  textAreaWrapper: {
    backgroundColor: DesktopTheme.colors.contentBg,
    borderRadius: DesktopTheme.borderRadius.md,
    borderWidth: 1,
    borderColor: DesktopTheme.colors.border,
  },
  textArea: {
    height: 280,
    padding: DesktopTheme.spacing.md,
    fontSize: DesktopTheme.typography.fontSize.body,
    fontFamily: DesktopTheme.typography.fontFamily.body,
    color: DesktopTheme.colors.textPrimary,
    textAlignVertical: 'top',
    outlineStyle: 'none',
  },
  attachmentsSection: {
    marginTop: DesktopTheme.spacing.sm,
  },
  uploadButtons: {
    flexDirection: 'row',
    gap: DesktopTheme.spacing.md,
  },
  uploadBtn: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: DesktopTheme.spacing.sm,
    paddingVertical: DesktopTheme.spacing.md,
    backgroundColor: DesktopTheme.colors.contentBg,
    borderRadius: DesktopTheme.borderRadius.md,
    borderWidth: 2,
    borderColor: DesktopTheme.colors.border,
    borderStyle: 'dashed',
  },
  uploadBtnHover: {
    borderColor: DesktopTheme.colors.primary,
    backgroundColor: DesktopTheme.colors.primaryLight,
  },
  uploadBtnDisabled: {
    backgroundColor: DesktopTheme.colors.border,
    borderStyle: 'solid',
  },
  uploadBtnText: {
    fontSize: DesktopTheme.typography.fontSize.body,
    fontFamily: DesktopTheme.typography.fontFamily.body,
    color: DesktopTheme.colors.primary,
    fontWeight: '600',
  },
  uploadBtnTextDisabled: {
    color: DesktopTheme.colors.textMuted,
  },
  previewArea: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: DesktopTheme.spacing.sm,
    marginTop: DesktopTheme.spacing.md,
  },
  previewItem: {
    position: 'relative',
  },
  previewImage: {
    width: 80,
    height: 80,
    borderRadius: DesktopTheme.borderRadius.md,
  },
  deleteBtn: {
    position: 'absolute',
    top: -8,
    right: -8,
    backgroundColor: DesktopTheme.colors.cardBg,
    borderRadius: 12,
    padding: 4,
    ...DesktopTheme.shadows.sm,
  },
  pdfPreviewItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: DesktopTheme.spacing.sm,
    backgroundColor: DesktopTheme.colors.contentBg,
    padding: DesktopTheme.spacing.sm,
    borderRadius: DesktopTheme.borderRadius.md,
    paddingRight: DesktopTheme.spacing.lg,
  },
  pdfName: {
    fontSize: DesktopTheme.typography.fontSize.small,
    fontFamily: DesktopTheme.typography.fontFamily.body,
    color: DesktopTheme.colors.textSecondary,
    maxWidth: 100,
  },
  deleteBtnPdf: {
    marginLeft: DesktopTheme.spacing.sm,
  },
  submitBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: DesktopTheme.spacing.sm,
    backgroundColor: DesktopTheme.colors.primary,
    paddingVertical: DesktopTheme.spacing.md,
    borderRadius: DesktopTheme.borderRadius.md,
    marginTop: DesktopTheme.spacing.lg,
    ...DesktopTheme.shadows.sm,
  },
  submitBtnHover: {
    backgroundColor: DesktopTheme.colors.primaryHover,
  },
  submitBtnText: {
    fontSize: DesktopTheme.typography.fontSize.body,
    fontFamily: DesktopTheme.typography.fontFamily.body,
    color: '#ffffff',
    fontWeight: '600',
  },
  supportCard: {
    width: 280,
    backgroundColor: DesktopTheme.colors.cardBg,
    borderRadius: DesktopTheme.borderRadius.xl,
    padding: DesktopTheme.spacing.xl,
    alignItems: 'center',
    ...DesktopTheme.shadows.card,
  },
  supportTitle: {
    fontSize: DesktopTheme.typography.fontSize.h4,
    fontFamily: DesktopTheme.typography.fontFamily.heading,
    color: DesktopTheme.colors.textPrimary,
    marginTop: DesktopTheme.spacing.md,
    marginBottom: DesktopTheme.spacing.sm,
    textAlign: 'center',
  },
  supportText: {
    fontSize: DesktopTheme.typography.fontSize.body,
    fontFamily: DesktopTheme.typography.fontFamily.body,
    color: DesktopTheme.colors.textSecondary,
    textAlign: 'center',
    marginBottom: DesktopTheme.spacing.lg,
  },
  callBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: DesktopTheme.spacing.sm,
    backgroundColor: DesktopTheme.colors.info,
    paddingVertical: DesktopTheme.spacing.md,
    paddingHorizontal: DesktopTheme.spacing.lg,
    borderRadius: DesktopTheme.borderRadius.md,
    ...DesktopTheme.shadows.sm,
  },
  callBtnHover: {
    backgroundColor: '#0284c7',
  },
  callBtnText: {
    fontSize: DesktopTheme.typography.fontSize.body,
    fontFamily: DesktopTheme.typography.fontFamily.body,
    color: '#ffffff',
    fontWeight: '600',
  },
});

const FeedbackPage = () => {
  const { companyId } = useContext(AuthContext);
  const { width } = useWindowDimensions();
  const isDesktop = Platform.OS === "web" && width > 768;
  const isDesktopWeb = useDesktopDetect();

  const [employeeId, setEmployeeId] = useState("");
  const [email, setEmail] = useState("");
  const [feedback, setFeedback] = useState("");
  const [images, setImages] = useState([]);
  const [pdf, setPdf] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const resetForm = () => {
    setEmployeeId("");
    setEmail("");
    setFeedback("");
    setImages([]);
    setPdf(null);
  };

  useEffect(() => {
    if (pdf) {
      console.log("📄 PDF state updated in useEffect:", pdf);
    } else {
      console.log("📄 PDF state is null");
    }
  }, [pdf]);

  useFocusEffect(
    useCallback(() => {
      resetForm();
    }, [])
  );

  const supportPhoneNumber = "tel:+917304073165";

  const showAlert = (title, message) => {
    if (Platform.OS === "web") {
      window.alert(`${title}\n\n${message}`);
    } else {
      showAlert(title, message);
    }
  };

  const handleImageUpload = async () => {
    const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
    if (status !== "granted") {
      showAlert("Permission required", "Please grant media library access.");
      return;
    }

    if (images.length >= 2) {
      showAlert("Maximum Upload","You can upload up to 2 images only.");
      return;
    }

    const result = await ImagePicker.launchImageLibraryAsync({
      base64: true,
      mediaTypes: ImagePicker.MediaTypeOptions.Images,
      quality: 0.5,
    });

    if (!result.canceled && result.assets?.length > 0) {
      const picked = result.assets[0];
      const newImage = {
        uri: picked.uri,
        base64: picked.base64,
        type: "image",
      };
      setImages((prevImages) => [...prevImages, newImage]);
    }
  };

  const handlePDFUpload = async () => {
    const result = await DocumentPicker.getDocumentAsync({
      type: "application/pdf",
      copyToCacheDirectory: true,
    });

    if (!result.canceled && result.assets && result.assets.length > 0) {
      const file = result.assets[0];
      const base64Pdf = await readAsBase64(file.uri);
      const newPdf = {
        uri: file.uri,
        base64: base64Pdf,
        type: "pdf",
        name: file.name,
      };
      setPdf(newPdf);
    } else {
      console.log("❌ PDF not picked or result invalid");
    }
  };

  const readAsBase64 = async (uri) => {
    const response = await fetch(uri);
    const blob = await response.blob();
    const reader = new FileReader();

    return new Promise((resolve, reject) => {
      reader.onerror = reject;
      reader.onload = () => {
        const base64String = reader.result.split(",")[1];
        resolve(base64String);
      };
      reader.readAsDataURL(blob);
    });
  };

  const handleDeleteImage = (index) => {
    const updatedImages = images.filter((_, i) => i !== index);
    setImages(updatedImages);
  };

  const handleDeletePdf = () => {
    setPdf(null);
  };

  const handleSubmit = async () => {
    if (!employeeId || !email || !feedback) {
      showAlert("Error","All fields are required.");
      return;
    }

    try {
      const feedbackData = {
        employeeId,
        email,
        feedback,
        images: images.map((img) => ({
          base64: img.base64,
          type: img.type,
        })),
        pdf: pdf ? { base64: pdf.base64, type: pdf.type } : null,
        timestamp: new Date(),
      };

      await setDoc(
        doc(db, `companies/${companyId}/feedback`, employeeId),
        feedbackData
      );
      showAlert("Success","Feedback submitted successfully!");
      resetForm();
    } catch (error) {
      console.error("Error submitting feedback:", error);
      showAlert("Error","Error submitting feedback.");
    }
  };

  const handleCall = () => {
    Linking.openURL(supportPhoneNumber);
  };

  // Desktop Web View with new theme
  if (isDesktopWeb) {
    return (
      <DesktopFeedbackView
        employeeId={employeeId}
        setEmployeeId={setEmployeeId}
        email={email}
        setEmail={setEmail}
        feedback={feedback}
        setFeedback={setFeedback}
        images={images}
        pdf={pdf}
        handleImageUpload={handleImageUpload}
        handlePDFUpload={handlePDFUpload}
        handleDeleteImage={handleDeleteImage}
        handleDeletePdf={handleDeletePdf}
        handleSubmit={handleSubmit}
        handleCall={handleCall}
        isSubmitting={isSubmitting}
      />
    );
  }

  // Mobile View
  return (
    <ScrollView contentContainerStyle={styles.container}>
      {isDesktop ? (
        <View style={styles.webRow}>
          {/* LEFT PANEL */}
          <View style={styles.leftPanel}>
            <Text style={styles.label}>Employee ID</Text>
            <TextInput
              value={employeeId}
              onChangeText={setEmployeeId}
              style={styles.input}
              placeholder="Enter your Employee ID"
              placeholderTextColor={GlobalStyles.inpColor.colors.placeholder}
              selectionColor={GlobalStyles.inpColor.colors.selection}
            />

            <Text style={styles.label}>Email</Text>
            <TextInput
              value={email}
              onChangeText={setEmail}
              style={styles.input}
              placeholder="Enter your Email"
              keyboardType="email-address"
              placeholderTextColor={GlobalStyles.inpColor.colors.placeholder}
              selectionColor={GlobalStyles.inpColor.colors.selection}
            />

            <View style={styles.uploadRow}>
              <TouchableOpacity
                style={[styles.uploadButton, pdf && styles.disabledButton]}
                onPress={handleImageUpload}
                disabled={!!pdf}
              >
                <Text style={styles.uploadText}>Upload Image</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[
                  styles.uploadButton,
                  images.length > 0 && styles.disabledButton,
                ]}
                onPress={handlePDFUpload}
                disabled={images.length > 0}
              >
                <Text style={styles.uploadText}>Upload PDF</Text>
              </TouchableOpacity>
            </View>
            <View style={styles.imageRowContainer}>
              {images.map((img, index) => (
                <View key={index} style={styles.imagePreview}>
                  <Image
                    source={{ uri: `data:image/jpeg;base64,${img.base64}` }}
                    style={{ width: 100, height: 100 }}
                  />
                  <TouchableOpacity
                    onPress={() => handleDeleteImage(index)}
                    style={{ marginLeft: 10 }}
                  >
                    <Ionicons name="trash" size={24} color="red" />
                  </TouchableOpacity>
                </View>
              ))}

              {/* ✅ Show uploaded PDF */}
              {pdf && (
                <View style={styles.pdfPreview}>
                  <TouchableOpacity
                    onPress={() =>
                      Linking.openURL(pdf.uri).catch((err) =>
                        showAlert("Failed to open PDF", err.message)
                      )
                    }
                    style={{ flex: 1 }}
                  >
                    <Text style={styles.pdfLinkWeb}>
                      {pdf.name || "View PDF"}
                    </Text>
                  </TouchableOpacity>
                  <TouchableOpacity onPress={handleDeletePdf}>
                    <Ionicons name="trash" size={24} color="red" />
                  </TouchableOpacity>
                </View>
              )}
            </View>
          </View>

          {/* RIGHT PANEL */}
          <View style={styles.rightPanel}>
            <Text style={styles.label}>Feedback (Max 1000 words)</Text>
            <TextInput
              value={feedback}
              onChangeText={(text) => {
                const wordCount = text.trim().split(/\s+/).length;
                if (wordCount <= 1000) setFeedback(text);
              }}
              style={[styles.textArea, { height: 200 }]}
              multiline
              placeholder="Write your feedback here..."
              placeholderTextColor={GlobalStyles.inpColor.colors.placeholder}
              selectionColor={GlobalStyles.inpColor.colors.selection}
            />

            <View style={styles.buttonRowWeb}>
              <TouchableOpacity
                style={styles.submitButtonWeb}
                onPress={handleSubmit}
              >
                <Text style={styles.submitText}>Submit Feedback</Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={styles.callButtonWeb}
                onPress={handleCall}
              >
                <Ionicons name="call" size={20} color="#fff" />
                <Text style={styles.callText}> Need help?</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      ) : (
        // MOBILE / MOBILE WEB LAYOUT
        <>
          <Text style={styles.label}>Employee ID</Text>
          <TextInput
            value={employeeId}
            onChangeText={setEmployeeId}
            style={styles.input}
            placeholder="Enter your Employee ID"
            placeholderTextColor={GlobalStyles.inpColor.colors.placeholder}
            selectionColor={GlobalStyles.inpColor.colors.selection}
          />

          <Text style={styles.label}>Email</Text>
          <TextInput
            value={email}
            onChangeText={setEmail}
            style={styles.input}
            placeholder="Enter your Email"
            keyboardType="email-address"
            placeholderTextColor={GlobalStyles.inpColor.colors.placeholder}
            selectionColor={GlobalStyles.inpColor.colors.selection}
          />

          <Text style={styles.label}>Feedback (Max 1000 words)</Text>
          <TextInput
            value={feedback}
            onChangeText={(text) => {
              const wordCount = text.trim().split(/\s+/).length;
              if (wordCount <= 1000) setFeedback(text);
            }}
            style={styles.textArea}
            multiline
            placeholder="Write your feedback here..."
            placeholderTextColor={GlobalStyles.inpColor.colors.placeholder}
            selectionColor={GlobalStyles.inpColor.colors.selection}
          />

          <View style={styles.uploadRow}>
            <TouchableOpacity
              style={[styles.uploadButton, pdf && styles.disabledButton]}
              onPress={handleImageUpload}
              disabled={!!pdf}
            >
              <Text style={styles.uploadText}>Upload Image</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[
                styles.uploadButton,
                images.length > 0 && styles.disabledButton,
              ]}
              onPress={handlePDFUpload}
              disabled={images.length > 0}
            >
              <Text style={styles.uploadText}>Upload PDF</Text>
            </TouchableOpacity>
          </View>
          <View style={styles.imageRowContainer}>
            {images.map((img, index) => (
              <View key={index} style={styles.imagePreview}>
                <Image
                  source={{ uri: `data:image/jpeg;base64,${img.base64}` }}
                  style={{ width: 100, height: 100 }}
                />
                <TouchableOpacity
                  onPress={() => handleDeleteImage(index)}
                  style={{ marginLeft: 10 }}
                >
                  <Ionicons name="trash" size={24} color="red" />
                </TouchableOpacity>
              </View>
            ))}

            {pdf && (
              <View style={styles.pdfPreview}>
                <TouchableOpacity
                  onPress={() =>
                    Linking.openURL(pdf.uri).catch((err) =>
                      showAlert("Failed to open PDF", err.message)
                    )
                  }
                  style={{ flex: 1 }}
                >
                  <Text style={styles.pdfLink}>{pdf.name || "View PDF"}</Text>
                </TouchableOpacity>
                <TouchableOpacity onPress={handleDeletePdf}>
                  <Ionicons name="trash" size={24} color="red" />
                </TouchableOpacity>
              </View>
            )}
          </View>

          <TouchableOpacity style={styles.submitButton} onPress={handleSubmit}>
            <Text style={styles.submitText}>Submit Feedback</Text>
          </TouchableOpacity>

          <TouchableOpacity style={styles.callButton} onPress={handleCall}>
            <Ionicons name="call" size={20} color="#fff" />
            <Text style={styles.callText}> Need help? Call us</Text>
          </TouchableOpacity>
        </>
      )}
    </ScrollView>
  );
};

export default FeedbackPage;

const styles = StyleSheet.create({
  container: {
    padding: 20,
    backgroundColor: GlobalStyles.GlobalBg.backgroundColor,
    flexGrow: 1,
  },
  label: {
    color: GlobalStyles.GlobalBg.headerColor,
    fontSize: GlobalStyles.fontSize.md,
    fontFamily: GlobalStyles.GlobalFont.fontFamily,
    marginBottom: 10,
    paddingVertical: 1,
    marginLeft: 5,
  },
  input: {
    width: "100%",
    height: 45,
    borderColor: "#ccc",
    fontSize: GlobalStyles.fontSize.xs,
    borderWidth: 1,
    borderRadius: 8,
    marginBottom: 16,
    paddingHorizontal: 12,
    backgroundColor: "#fff",
    fontFamily: GlobalStyles.inpFont.fontFamily,
  },
  textArea: {
    borderColor: "#ccc",
    borderWidth: 1,
    padding: 10,
    marginTop: 5,
    fontSize: GlobalStyles.fontSize.xs,
    height: wp("40%"),
    textAlignVertical: "top",
    borderRadius: 8,
    backgroundColor: "#fff",
    fontFamily: GlobalStyles.inpFont.fontFamily,
    marginBottom: 16,
  },
  uploadRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 10,
  },
  uploadButton: {
    backgroundColor: GlobalStyles.btnColor.backgroundColor,
    padding: 10,
    borderRadius: 8,
    width: "48%",
    alignItems: "center",
  },
  disabledButton: {
    backgroundColor: "#ccc",
  },
  uploadText: {
    color: "#fff",
    fontSize: GlobalStyles.fontSize.topTitle,
    fontFamily: GlobalStyles.GlobalFont.fontFamily,
  },
  submitButton: {
    width: wp("90%"),
    backgroundColor: GlobalStyles.btnColor.backgroundColor,
    borderRadius: 8,
    paddingVertical: 12,
    alignItems: "center",
    marginBottom: 5,
    marginTop: 10,
  },
  submitText: {
    color: "#fff",
    fontSize: GlobalStyles.fontSize.btn,
    fontFamily: GlobalStyles.GlobalFont.fontFamily,
  },
  callButton: {
    marginTop: 20,
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: GlobalStyles.btnColor.backgroundColor,
    padding: 12,
    borderRadius: 8,
    justifyContent: "center",
  },
  callText: {
    color: "#fff",
    fontSize: GlobalStyles.fontSize.btn,
    fontFamily: GlobalStyles.GlobalFont.fontFamily,
  },
  webRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    gap: 20,
  },
  leftPanel: {
    width: "45%",
  },
  rightPanel: {
    width: "50%",
  },
  imageRowContainer: {
    flexDirection: "row",
    flexWrap: "wrap", // wrap to next row if needed
    gap: 10,
    marginTop: 12,
  },
  imagePreview: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 10,
  },
  pdfPreview: {
    flexDirection: "row",
    alignItems: "center",
    marginTop: 10,
  },
  pdfLink: {
    color: "blue",
    textDecorationLine: "underline",
  },
  buttonRowWeb: {
    flexDirection: "row",
    justifyContent: "space-between",
    gap: 16,
    marginTop: 16,
  },
  submitButtonWeb: {
    flex: 1,
    backgroundColor: GlobalStyles.btnColor.backgroundColor,
    paddingVertical: 12,
    borderRadius: 8,
    alignItems: "center",
  },
  callButtonWeb: {
    flex: 1,
    backgroundColor: GlobalStyles.btnColor.backgroundColor,
    paddingVertical: 12,
    borderRadius: 8,
    alignItems: "center",
    flexDirection: "row",
    justifyContent: "center",
  },
});
